import { api } from "../../api/index.ts";
import { useCallback } from "react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { extractApiError } from "../../api/errors.ts";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import { CompanyEntry } from "../../types/shared.ts";
import {
  companiesCreateToExternal,
  companiesUpdateToExternal,
} from "../../pages/companies/converts.ts";

export const useSetCompanyData = () => {
  const showToast = useShowToast();

  const [triggerCreate] =
    api.endpoints.createCompanyApiCompaniesPost.useMutation();
  const [triggerUpdate] =
    api.endpoints.updateCompanyApiCompaniesCompanyIdPut.useMutation();
  const [triggerDelete] =
    api.endpoints.deleteCompanyApiCompaniesCompanyIdDelete.useMutation();

  const setData = useCallback(
    async (updatedRows: Partial<CompanyEntry>[]) => {
      return await Promise.all(
        updatedRows.map((row) => {
          if (row.id) {
            const update = companiesUpdateToExternal(row);
            if (Object.entries(update).length !== 0) {
              return triggerUpdate({
                companyId: row.id,
                companyUpdate: update,
              });
            } else {
              return undefined;
            }
          } else {
            return triggerCreate({
              companyCreate: companiesCreateToExternal(row as CompanyEntry),
            });
          }
        }),
      )
        .then((results) => {
          console.info("Update results ", results);
          results.forEach((result) => {
            if (result && "error" in result) {
              showToast({
                severity: "error",
                summary: "Błąd",
                detail: extractApiError(
                  result.error as FetchBaseQueryError | SerializedError,
                ),
              });
            }
          });
          return results;
        })
        .catch((err) => {
          console.error(err);
          return null;
        });
    },
    [triggerCreate, triggerUpdate, showToast],
  );

  const deleteData = useCallback(
    async (companyId: number) => {
      try {
        const result = await triggerDelete({ companyId });
        console.info("Deleted company", companyId, result);
        return result;
      } catch (error) {
        console.error("Delete error", error);
        throw error;
      }
    },
    [triggerDelete],
  );

  return { setData, deleteData };
};
